import {
  ColumnDef,
  RowSelectionState,
  VisibilityState,
  ColumnFiltersState,
  SortingState,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import useLocalStorage from 'use-local-storage';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

export type CursorPaginationProps = {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  totalCount?: number;
  currentCount?: number;
};

export function DataTable<TData>({
  id,
  data,
  columns,
  showGlobalFilter = true,
  globalFilterPlaceholder = 'Filter...',
  showColumnVisibility = true,
  cursorPagination,
}: {
  id: string;
  data: TData[];
  columns: ColumnDef<TData>[];
  showGlobalFilter?: boolean;
  globalFilterPlaceholder?: string;
  showColumnVisibility?: boolean;
  cursorPagination?: CursorPaginationProps;
}) {
  const [sorting, setSorting] = useLocalStorage<SortingState>(
    `${id}-sorting`,
    [] as SortingState,
  );
  const [columnFilters, setColumnFilters] = useLocalStorage<ColumnFiltersState>(
    `${id}-column-filters`,
    [] as ColumnFiltersState,
  );
  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>(
      `${id}-column-visibility`,
      {} as VisibilityState,
    );
  const [rowSelection, setRowSelection] = useLocalStorage<RowSelectionState>(
    `${id}-row-selection`,
    {} as RowSelectionState,
  );

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: cursorPagination
        ? undefined
        : {
            pageIndex: 0,
            pageSize: 50,
          },
    },
    onSortingChange: (updater) => setSorting(updater as SortingState),
    onColumnFiltersChange: (updater) =>
      setColumnFilters(updater as ColumnFiltersState),
    onColumnVisibilityChange: (updater) =>
      setColumnVisibility(updater as VisibilityState),
    onRowSelectionChange: (updater) =>
      setRowSelection(updater as RowSelectionState),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: cursorPagination
      ? undefined
      : getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center py-4">
        {showGlobalFilter && (
          <Input
            placeholder={globalFilterPlaceholder}
            value={(table.getState().globalFilter as string) ?? ''}
            onChange={(event) => table.setGlobalFilter(event.target.value)}
            className="max-w-sm"
          />
        )}
        {showColumnVisibility && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDownIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHead>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="bg-muted">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHeader key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHeader>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {cursorPagination ? (
            <>
              {cursorPagination.currentCount} of{' '}
              {cursorPagination.totalCount || '?'} row(s)
              {table.getFilteredSelectedRowModel().rows.length > 0 && (
                <span className="ml-2">
                  ({table.getFilteredSelectedRowModel().rows.length} selected)
                </span>
              )}
            </>
          ) : (
            <>
              {table.getFilteredSelectedRowModel().rows.length} of{' '}
              {table.getFilteredRowModel().rows.length} row(s) selected.
            </>
          )}
        </div>
        <div className="space-x-2">
          {cursorPagination ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={cursorPagination.onPreviousPage}
                disabled={!cursorPagination.hasPreviousPage}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={cursorPagination.onNextPage}
                disabled={!cursorPagination.hasNextPage}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
